import _ from 'lodash'
import { useCallback, useMemo } from 'react'
import {
  ColDef,
  ColumnState,
  FilterChangedEvent,
  GridApi,
  RowGroupOpenedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { useBulkSheetState as useBulkSheetStateBase } from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'

export const useBulkSheetState = (
  gridApi: GridApi | undefined,
  functionUuid: string,
  uiStateKey: UiStateKey
) => {
  const base = useBulkSheetStateBase(functionUuid, uiStateKey)

  const restoreColumnState = useCallback(() => {
    if (!gridApi || _.isEmpty(base.columnState)) return
    gridApi.applyColumnState({
      state: base.columnState,
      applyOrder: true,
    })
  }, [gridApi, base.columnState])

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi?.getColumnState()
    columnState && base.saveColumnState(columnState)
  }, [gridApi, base])

  const restoreExpandedRows = useCallback(() => {
    if (_.isEmpty(base.expandedRowIds) || !gridApi) return
    setTimeout(() => {
      base.expandedRowIds.forEach(id => {
        gridApi?.getRowNode(id)?.setExpanded(true)
      })
    }, 200)
  }, [base.expandedRowIds, gridApi])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api.getFilterModel()
      delete filterModel['uuid']
      base.saveFilterState(filterModel)
      return Object.keys(filterModel)
        .map(col => e.api.getColumnDef(col))
        .filter(v => !!v && v.field !== 'uuid') as ColDef[]
    },
    [base]
  )

  const onFirstDataRendered = useCallback(
    e => {
      restoreExpandedRows()
      restoreColumnState()
      if (!_.isEmpty(base.filterState)) {
        gridApi?.setFilterModel(base.filterState ?? null)
      }
      base.finishRestoring()
    },
    [gridApi, base, restoreExpandedRows, restoreColumnState]
  )

  const onGridReady = useCallback(() => {
    restoreColumnState()
  }, [restoreColumnState])

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent) => {
      const key = event.node.key
      if (!key) return
      if (event.expanded) {
        base.expandRows([key])
      } else {
        base.collapseRows([key])
      }
    },
    [base]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      rememberColumnState()
      const sortedColumnStates = e.api
        .getColumnState()
        .filter((colState: ColumnState) => !!colState.sort)
        .map((colState: ColumnState) => {
          const colDef: ColDef | null = e.api.getColumnDef(colState.colId)
          if (!colDef) return
          return {
            colId: colState.colId,
            field: colDef,
            headerName: colDef.headerName,
            sort: colState.sort,
          } as SortedColumnState
        })
        .filter(v => !!v)
      e.api.setGridOption('suppressRowDrag', 0 < sortedColumnStates.length)
      return sortedColumnStates
    },
    [rememberColumnState]
  )

  const columnSettingUiStateKey = useMemo(() => {
    switch (uiStateKey) {
      case UiStateKey.ResourcePlanCrossProjectsMemberView:
        return UiStateKey.BulkSheetUIStateColumnAndFilterResourcePlanCPMemberView
      case UiStateKey.ResourcePlanCrossProjectsProjectView:
        return UiStateKey.BulkSheetUIStateColumnAndFilterResourcePlanCPProjectView
      default:
        return UiStateKey.BulkSheetUIStateColumnAndFilterResourcePlanCPMemberView
    }
  }, [uiStateKey])

  return {
    base,
    restoreColumnState,
    rememberColumnState,
    restoreExpandedRows,
    onFilterChanged,
    onFirstDataRendered,
    onGridReady,
    onRowGroupOpened,
    onSortChanged,
    columnSettingUiStateKey,
  }
}
